/**
 * Centralized logging utility that respects environment configuration
 * All console.log statements should be replaced with logger methods
 *
 * Usage:
 *   import logger from '@/utils/logger';
 *   logger.debug('Debug info', data);  // Only in development
 *   logger.info('Info message', data);  // Only in development
 *   logger.warn('Warning', data);       // All environments
 *   logger.error('Error', error);       // All environments (sanitized in production)
 */

import { config } from '../config/env';

class Logger {
  constructor() {
    this.isDebugEnabled = true; // Force debug to always be enabled for debugging
    this.isProduction = config.MODE === 'production';
  }

  /**
   * Debug logs - now shown in all environments for debugging
   * Use for detailed debugging information
   */
  debug(...args) {
    console.log('[DEBUG]', ...args);
  }

  /**
   * Info logs - now shown in all environments for debugging
   * Use for general information flow
   */
  info(...args) {
    console.info('[INFO]', ...args);
  }

  /**
   * Warning logs - shown in all environments
   * Use for recoverable errors or deprecated features
   */
  warn(...args) {
    console.warn('[WARN]', ...args);
  }

  /**
   * Error logs - shown in all environments
   * Use for errors that need attention
   * Now showing full details in all environments for debugging
   */
  error(...args) {
    console.error('[ERROR]', ...args);
  }

  /**
   * Sanitize sensitive data in production
   * Redacts sensitive keys from objects
   */
  sanitize(data) {
    if (typeof data === 'object' && data !== null) {
      // Handle Error objects
      if (data instanceof Error) {
        return {
          message: data.message,
          type: data.name,
          // Don't include stack trace in production
        };
      }

      // Handle Arrays
      if (Array.isArray(data)) {
        return data.map(item => this.sanitize(item));
      }

      // Handle plain objects
      const sanitized = {};

      // List of sensitive keys to redact
      const sensitiveKeys = [
        'tenant_id',
        'tenantId',
        'session_id',
        'sessionId',
        'thread_id',
        'threadId',
        'file_id',
        'fileId',
        'user_id',
        'userId',
        'token',
        'password',
        'api_key',
        'apiKey',
        'jwt',
        'authorization',
        'auth',
        'common_sid',
        'commonSid',
        'athena_sid',
        'athenaSid',
      ];

      for (const [key, value] of Object.entries(data)) {
        if (sensitiveKeys.includes(key)) {
          sanitized[key] = '[REDACTED]';
        } else if (typeof value === 'object' && value !== null) {
          sanitized[key] = this.sanitize(value);
        } else {
          sanitized[key] = value;
        }
      }

      return sanitized;
    }

    return data;
  }

  /**
   * Group logs - only in development
   */
  group(label) {
    if (this.isDebugEnabled) {
      console.group(label);
    }
  }

  groupCollapsed(label) {
    if (this.isDebugEnabled) {
      console.groupCollapsed(label);
    }
  }

  groupEnd() {
    if (this.isDebugEnabled) {
      console.groupEnd();
    }
  }

  /**
   * Table logs - only in development
   */
  table(data) {
    if (this.isDebugEnabled) {
      console.table(data);
    }
  }

  /**
   * Time logs - only in development
   */
  time(label) {
    if (this.isDebugEnabled) {
      console.time(label);
    }
  }

  timeEnd(label) {
    if (this.isDebugEnabled) {
      console.timeEnd(label);
    }
  }

  /**
   * Assert - only in development
   */
  assert(condition, ...args) {
    if (this.isDebugEnabled) {
      console.assert(condition, ...args);
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export default for convenient importing
export default logger;
